import api from './client';
import { User, UserRole } from '../types/user';

export interface UserCreateData {
  email: string;
  password: string;
  name: string;
  role: UserRole;
  phone?: string;
  company_id?: string;
  status?: 'active' | 'inactive';
}

export interface UserUpdateData {
  name?: string;
  phone?: string;
  status?: 'active' | 'inactive';
  role?: UserRole;
  password?: string;
  company_id?: string;
}

export interface UsersListResponse {
  success: boolean;
  data: {
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasMore: boolean;
    };
  };
}

export interface UserResponse {
  success: boolean;
  data: User;
}

export interface UsersQueryParams {
  role?: UserRole;
  status?: 'active' | 'inactive';
  search?: string;
  company_id?: string;
}

export const usersApi = {
  /**
   * List users with optional filters
   */
  async listUsers(params?: UsersQueryParams): Promise<User[]> {
    const timestamp = new Date().getTime();
    const queryParams = new URLSearchParams({ _t: timestamp.toString() });

    if (params?.role) queryParams.append('role', params.role);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.company_id) queryParams.append('company_id', params.company_id);

    const response = await api.get<UsersListResponse>(`/api/users?${queryParams.toString()}`);
    return response.data.data.users;
  },

  /**
   * Create a new user
   */
  async createUser(data: UserCreateData): Promise<User> {
    const response = await api.post<UserResponse>('/api/users', data);
    return response.data.data;
  },

  /**
   * Get user by ID
   */
  async getUser(id: string): Promise<User> {
    const response = await api.get<UserResponse>(`/api/users/${id}`);
    return response.data.data;
  },

  /**
   * Update user information
   */
  async updateUser(id: string, data: UserUpdateData): Promise<User> {
    const response = await api.patch<UserResponse>(`/api/users/${id}`, data);
    return response.data.data;
  },

  /**
   * Delete user (soft delete - set status to inactive)
   */
  async deleteUser(id: string): Promise<void> {
    await api.delete(`/api/users/${id}`);
  },
};
